import { ChangeDetectionStrategy, Component, computed, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ProjectEnvironment } from '../../../models/project-environment.model';
import { ProjectTestStage } from '../../../models/project.model';

export interface UmgebungZeile {
  schluessel: string;
  name: string;
  url: string;
}

export interface StufenZeile {
  name: string;
  an: boolean;
  umgebung: string | null;
}

export interface Schritt2Daten {
  umgebungen: UmgebungZeile[];
  stufen: StufenZeile[];
}

let zaehler = 0;

function neueUmgebung(name = ''): UmgebungZeile {
  return { schluessel: `neu-${++zaehler}`, name, url: '' };
}

function neueStufe(umgebungen: readonly UmgebungZeile[]): StufenZeile {
  return { name: '', an: true, umgebung: umgebungen[0]?.schluessel ?? null };
}

function vorbelegteUmgebungen(translate: TranslateService): UmgebungZeile[] {
  return ['test', 'staging', 'produktion'].map((k) =>
    neueUmgebung(translate.instant(`projektNeu.umgebungen.vorbelegung.${k}`)),
  );
}

export function schritt2Vorbelegung(translate: TranslateService): Schritt2Daten {
  const umgebungen = vorbelegteUmgebungen(translate);
  const [test, staging] = umgebungen.map((u) => u.schluessel);
  const stufe = (k: string, an: boolean, umgebung: string): StufenZeile => ({
    name: translate.instant(`projektNeu.teststufen.vorbelegung.${k}`),
    an,
    umgebung,
  });
  return {
    umgebungen,
    stufen: [
      stufe('system', true, test),
      stufe('abnahme', true, staging),
      stufe('integration', false, test),
      stufe('komponente', false, test),
    ],
  };
}

export function schritt2AusVorlage(
  umgebungen: readonly ProjectEnvironment[],
  stufen: readonly ProjectTestStage[],
  translate: TranslateService,
): Schritt2Daten {
  const nachPosition = <T extends { position: number }>(liste: readonly T[]) =>
    [...liste].sort((a, b) => a.position - b.position);
  // Ohne Umgebungen bekäme das Projekt beim Anlegen die Standard-Umgebungen des Backends; die Vorbelegung zeigt
  // stattdessen sichtbar, was gespeichert wird.
  const zeilen = umgebungen.length
    ? nachPosition(umgebungen).map((u) => ({ schluessel: u.id, name: u.name, url: u.url ?? '' }))
    : vorbelegteUmgebungen(translate);
  const bekannt = new Set(zeilen.map((z) => z.schluessel));
  return {
    umgebungen: zeilen,
    stufen: stufen.length
      ? nachPosition(stufen).map((s) => ({
          name: s.name,
          an: true,
          umgebung: s.environmentId && bekannt.has(s.environmentId) ? s.environmentId : null,
        }))
      : [neueStufe(zeilen)],
  };
}

export function doppelteUmgebungen(umgebungen: readonly UmgebungZeile[]): Set<number> {
  const gesehen = new Set<string>();
  const doppelt = new Set<number>();
  umgebungen.forEach((u, i) => {
    const name = u.name.trim().toLowerCase();
    if (!name) return;
    if (gesehen.has(name)) doppelt.add(i);
    gesehen.add(name);
  });
  return doppelt;
}

@Component({
  selector: 'app-umgebungen-teststufen-schritt',
  standalone: true,
  imports: [FormsModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule, TranslatePipe],
  templateUrl: './umgebungen-teststufen-schritt.component.html',
  styleUrl: './umgebungen-teststufen-schritt.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UmgebungenTeststufenSchrittComponent {
  readonly daten = model.required<Schritt2Daten>();

  readonly doppelt = computed(() => doppelteUmgebungen(this.daten().umgebungen));

  private readonly matcher: ErrorStateMatcher[] = [];

  fehlerMatcher(index: number): ErrorStateMatcher {
    return (this.matcher[index] ??= { isErrorState: () => this.doppelt().has(index) });
  }

  umgebungAendern(index: number, aenderung: Partial<UmgebungZeile>): void {
    this.daten.update((d) => ({
      ...d,
      umgebungen: d.umgebungen.map((u, i) => (i === index ? { ...u, ...aenderung } : u)),
    }));
  }

  umgebungHinzufuegen(): void {
    this.daten.update((d) => ({ ...d, umgebungen: [...d.umgebungen, neueUmgebung()] }));
  }

  umgebungEntfernen(index: number): void {
    this.daten.update((d) => {
      const entfernt = d.umgebungen[index].schluessel;
      return {
        umgebungen: d.umgebungen.filter((_, i) => i !== index),
        stufen: d.stufen.map((s) => (s.umgebung === entfernt ? { ...s, umgebung: null } : s)),
      };
    });
  }

  stufeAendern(index: number, aenderung: Partial<StufenZeile>): void {
    this.daten.update((d) => ({ ...d, stufen: d.stufen.map((s, i) => (i === index ? { ...s, ...aenderung } : s)) }));
  }

  stufeHinzufuegen(): void {
    this.daten.update((d) => ({ ...d, stufen: [...d.stufen, neueStufe(d.umgebungen)] }));
  }

  stufeEntfernen(index: number): void {
    this.daten.update((d) => ({ ...d, stufen: d.stufen.filter((_, i) => i !== index) }));
  }

  wert(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }
}
